import { useEffect, useMemo, useState } from 'react';
import { X, Loader2, AlertTriangle, FileText, ExternalLink, CheckCircle2 } from 'lucide-react';
import sprayService from '../../services/spray';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import type { LabelProposal, LabelProposalRow } from '../../types/spray';
import Button from '../ui/Button';

interface Props {
  chemicalId: number;
  productName: string;
  onClose: () => void;
  onApplied: () => void;
}

type Phase = 'reading' | 'review' | 'applying' | 'done' | 'error';

function rateLabel(row: LabelProposalRow): string {
  if (row.min_rate_per_ha === null && row.max_rate_per_ha === null) return '—';
  const lo = row.min_rate_per_ha;
  const hi = row.max_rate_per_ha;
  const span = lo === hi ? `${lo}` : `${lo ?? '?'}–${hi ?? '?'}`;
  return `${span} ${row.rate_unit ?? ''}`.trim();
}

function whpLabel(row: LabelProposalRow): string {
  const bits: string[] = [];
  if (row.whp_days !== null) bits.push(`H ${row.whp_days}d`);
  if (row.whp_export_days !== null) bits.push(`G ${row.whp_export_days}d`);
  return bits.length ? bits.join(' / ') : '—';
}

/**
 * Review screen for the APVMA label reader.
 *
 * Every proposed value is shown beside the label text it came from, and the
 * page number links into the PDF. That pairing is the point of the screen: a
 * withholding period read off a table feeds the spray gate, so the reviewer
 * needs to check the reading, not just the number.
 */
function LabelImportModal({ chemicalId, productName, onClose, onApplied }: Props) {
  const [phase, setPhase] = useState<Phase>('reading');
  const [proposal, setProposal] = useState<LabelProposal | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [takeMetadata, setTakeMetadata] = useState(true);
  const [result, setResult] = useState<{ registrationsUpdated: number; errors: { message: string }[] } | null>(null);

  // Applying is gated on a spray admin seat server-side. Knowing that here only
  // changes the wording — the server is what enforces it.
  const { hasAccess: isSprayAdmin, loading: licenseLoading } = useModuleLicense('spray', 'admin');

  useEffect(() => {
    let cancelled = false;
    sprayService.readApvmaLabel(chemicalId).then(res => {
      if (cancelled) return;
      if (!res.success || !res.data) {
        setError(res.error?.message || 'Could not read the label.');
        setPhase('error');
        return;
      }
      setProposal(res.data);
      // Default to the rows that are ready AND would not overwrite a rate
      // somebody already entered.
      setPicked(new Set(
        res.data.rows.filter(r => r.applicable && !r.match.had_rate).map(r => r.key),
      ));
      setPhase('review');
    }).catch((e: Error) => {
      if (cancelled) return;
      setError(e.message || 'Could not read the label.');
      setPhase('error');
    });
    return () => { cancelled = true; };
  }, [chemicalId]);

  const metadataFields = useMemo(
    () => Object.entries(proposal?.chemical ?? {}).filter(([, v]) => v !== null && v !== undefined),
    [proposal],
  );

  const toggle = (key: string) => {
    setPicked(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const applicableRows = proposal?.rows.filter(r => r.applicable) ?? [];
  const allPicked = applicableRows.length > 0 && applicableRows.every(r => picked.has(r.key));

  const apply = async () => {
    if (!proposal) return;
    setPhase('applying');
    const rows = proposal.rows
      .filter(r => r.applicable && picked.has(r.key))
      .map(r => ({
        registration_id: r.match.registration_id as number,
        page: r.page,
        min_rate_per_ha: r.min_rate_per_ha,
        max_rate_per_ha: r.max_rate_per_ha,
        rate_unit: r.rate_unit ?? undefined,
        whp_days: r.whp_days,
        whp_export_days: r.whp_export_days,
        rei_hours: r.rei_hours,
        notes: r.notes,
      }));

    const res = await sprayService.applyApvmaLabel(chemicalId, {
      label_url: proposal.label.url,
      chemical: takeMetadata && metadataFields.length ? proposal.chemical : undefined,
      rows,
    });

    if (!res.success || !res.data) {
      setError(res.error?.message || 'Could not save the label data.');
      setPhase('error');
      return;
    }
    setResult(res.data);
    setPhase('done');
    onApplied();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl my-8">
        <div className="flex items-start justify-between p-5 border-b border-neutral-200">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary-600 shrink-0" />
              Read the APVMA label
            </h2>
            <p className="text-sm text-neutral-500 mt-0.5 break-words">{productName}</p>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700 shrink-0" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5">
          {phase === 'reading' && (
            <div className="flex items-center gap-3 text-sm text-neutral-600 py-8 justify-center">
              <Loader2 className="h-5 w-5 animate-spin" />
              Downloading the approved label and reading its Directions for Use…
            </div>
          )}

          {phase === 'error' && (
            <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <p>{error}</p>
            </div>
          )}

          {phase === 'done' && result && (
            <div className="space-y-3">
              <div className="flex items-start gap-2 text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg p-3">
                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                <p>
                  {result.registrationsUpdated} registration{result.registrationsUpdated === 1 ? '' : 's'} updated.
                  They are marked <strong>Proposed</strong> until someone confirms them against the label.
                </p>
              </div>
              {result.errors.length > 0 && (
                <ul className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
                  {result.errors.map((e, i) => <li key={i}>{e.message}</li>)}
                </ul>
              )}
              <div className="flex justify-end">
                <Button onClick={onClose}>Close</Button>
              </div>
            </div>
          )}

          {(phase === 'review' || phase === 'applying') && proposal && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-neutral-500">
                <span>{proposal.summary.rows_parsed} label rows across {proposal.label.pages} pages</span>
                <span className="text-green-700">{proposal.summary.ready} ready to apply</span>
                {proposal.summary.unmatched > 0 && (
                  <span className="text-amber-700">{proposal.summary.unmatched} with no matching registration</span>
                )}
                {proposal.summary.no_rate_parsed > 0 && (
                  <span className="text-amber-700">{proposal.summary.no_rate_parsed} with no readable rate</span>
                )}
                <a
                  href={proposal.label.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:underline inline-flex items-center gap-1"
                >
                  Open the label <ExternalLink className="h-3 w-3" />
                </a>
              </div>

              {proposal.summary.rows_parsed === 0 && (
                <div className="flex items-start gap-2 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <p>
                    No Directions-for-Use table could be read from this label. Older and retail-pack
                    labels are often scans with no text layer, and some put their rate table in a
                    form with no ruling lines to follow. The rates will have to be entered by hand.
                  </p>
                </div>
              )}

              {metadataFields.length > 0 && (
                <div className="border border-neutral-200 rounded-lg p-3">
                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={takeMetadata}
                      onChange={e => setTakeMetadata(e.target.checked)}
                      className="mt-1 rounded border-neutral-300"
                    />
                    <span>
                      <span className="font-medium text-neutral-800">Also fill in {metadataFields.length} empty product field{metadataFields.length === 1 ? '' : 's'}</span>
                      <span className="block text-xs text-neutral-500 mt-1">
                        {metadataFields.map(([k, v]) => (
                          <span key={k} className="inline-block mr-3">
                            {k.replace(/_/g, ' ')}: <strong>{Array.isArray(v) ? v.join(', ') : String(v).slice(0, 80)}</strong>
                          </span>
                        ))}
                      </span>
                    </span>
                  </label>
                  {proposal.chemical_skipped.length > 0 && (
                    <p className="text-xs text-neutral-400 mt-2">
                      Left alone because the record already has a value: {proposal.chemical_skipped.map(s => s.field.replace(/_/g, ' ')).join(', ')}.
                    </p>
                  )}
                </div>
              )}

              {proposal.rows.length > 0 && (
                <div className="border border-neutral-200 rounded-lg overflow-hidden">
                  <div className="flex items-center justify-between px-3 py-2 bg-neutral-50 border-b border-neutral-200">
                    <span className="text-xs font-medium text-neutral-600">Directions for Use</span>
                    <button
                      onClick={() => setPicked(allPicked ? new Set() : new Set(applicableRows.map(r => r.key)))}
                      className="text-xs text-primary-600 hover:underline"
                    >
                      {allPicked ? 'Select none' : 'Select all ready'}
                    </button>
                  </div>
                  <div className="overflow-x-auto max-h-[45vh] overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-neutral-50 sticky top-0">
                        <tr className="text-neutral-500 text-left">
                          <th className="p-2 w-8"></th>
                          <th className="p-2">Registration</th>
                          <th className="p-2">Label says</th>
                          <th className="p-2 whitespace-nowrap">Rate</th>
                          <th className="p-2 whitespace-nowrap">WHP</th>
                          <th className="p-2 w-12">Page</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-100">
                        {proposal.rows.map(row => {
                          const usable = row.applicable;
                          return (
                            <tr key={row.key} className={usable ? '' : 'bg-neutral-50/60 text-neutral-400'}>
                              <td className="p-2 align-top">
                                <input
                                  type="checkbox"
                                  disabled={!usable}
                                  checked={picked.has(row.key)}
                                  onChange={() => toggle(row.key)}
                                  className="mt-0.5 rounded border-neutral-300 disabled:opacity-40"
                                />
                              </td>
                              <td className="p-2 align-top">
                                {row.match.kind === 'update' ? (
                                  <>
                                    <p className="font-medium text-neutral-800">{row.match.situation_name}</p>
                                    <p className="text-neutral-500">{row.match.target_pest}</p>
                                    {row.match.had_rate && (
                                      <span className="inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] bg-amber-100 text-amber-800 border border-amber-200">
                                        already has a rate — would overwrite
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  <span className="italic">no matching registration</span>
                                )}
                              </td>
                              <td className="p-2 align-top">
                                <p>{row.crop_text}</p>
                                <p className="text-neutral-500">{row.pest_text}</p>
                                <p className="text-neutral-400 mt-0.5">{row.rate_text}</p>
                              </td>
                              <td className="p-2 align-top whitespace-nowrap font-medium text-neutral-800">
                                {rateLabel(row)}
                              </td>
                              <td className="p-2 align-top whitespace-nowrap">{whpLabel(row)}</td>
                              <td className="p-2 align-top">
                                <a
                                  href={`${proposal.label.url}#page=${row.page}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary-600 hover:underline"
                                >
                                  p{row.page}
                                </a>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {!licenseLoading && !isSprayAdmin && (
                <div className="flex items-start gap-2 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <p>
                    Saving label-read rates needs a spray <strong>admin</strong> seat. These values set
                    withholding periods, which decide whether a spray can be created at all, so the
                    check belongs with whoever is accountable for that.
                  </p>
                </div>
              )}

              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-neutral-500">
                  Applied rows are marked <strong>Proposed</strong> until confirmed — they are visible everywhere but flagged as machine-read.
                </p>
                <div className="flex gap-2 shrink-0">
                  <Button variant="outline" onClick={onClose} disabled={phase === 'applying'}>Cancel</Button>
                  <Button
                    onClick={apply}
                    disabled={phase === 'applying' || !isSprayAdmin || (picked.size === 0 && !(takeMetadata && metadataFields.length))}
                  >
                    {phase === 'applying' && <Loader2 className="h-4 w-4 animate-spin" />}
                    Apply {picked.size > 0 ? `${picked.size} row${picked.size === 1 ? '' : 's'}` : ''}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LabelImportModal;
